import { ReactNode, useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import native from "src/constants/native";
import useDevice from "src/hooks/useDevice";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { paddingLarge, paddingXXXL } from "src/styles/spacing";
import { Text, useSafeArea } from "src/swsh-native";
import CloseModalButton from "../components/CloseModalButton";
import { ModalVariant } from "../types";

const ModalWrapperWithHeader = ({
	variant,
	children,
	title,
	style,
	ignoreHorizontalContainerPadding,
	ignoreBottomPadding,
	hasCloseButton = true,
	containerStyle,
}: {
	variant: ModalVariant;
	children?: ReactNode;
	title?: string;
	style?: StyleProp<ViewStyle>;
	ignoreHorizontalContainerPadding?: boolean;
	ignoreBottomPadding?: boolean;
	/**
	 * @default true
	 */
	hasCloseButton?: boolean;
	containerStyle?: StyleProp<ViewStyle>;
}) => {
	const { device } = useDevice();
	const safeArea = useSafeArea();

	const paddingStyle = useMemo<ViewStyle>(() => {
		if (
			device === "desktop" &&
			(variant === "Tall" ||
				variant === "Full" ||
				variant === "FullBlur" ||
				variant === "Blur")
		) {
			return {
				paddingHorizontal: paddingXXXL,
				paddingBottom: paddingXXXL + safeArea.bottom,
				paddingTop: paddingXXXL + safeArea.top,
			};
		}
		switch (variant) {
			case "Full":
			case "Blur":
			case "FullBlur":
			case "FullFull":
			case "FullEmpty":
				return {
					paddingHorizontal: paddingLarge,
					paddingBottom: paddingLarge + safeArea.bottom,
					paddingTop: paddingLarge + safeArea.top,
				};
			case "ShortBlur":
			case "Short":
			case "Tall":
				return {
					padding: paddingLarge,
					paddingBottom: paddingLarge + safeArea.bottom,
				};
			default:
				return enumNever(variant);
		}
	}, [variant, safeArea.bottom, safeArea.top, device]);

	return (
		<View
			style={[
				{
					flex: 1,
				},
				style,
				paddingStyle,
				ignoreHorizontalContainerPadding && {
					paddingHorizontal: 0,
				},
				ignoreBottomPadding && {
					paddingBottom: 0,
				},
				containerStyle,
			]}
		>
			<View
				style={[
					{
						flexDirection: "row",
						width: "100%",
						alignItems: "center",
						justifyContent: device === "desktop" ? "flex-start" : "center",
						marginBottom: paddingLarge,
						marginTop: paddingLarge,
					},
					ignoreHorizontalContainerPadding && {
						paddingHorizontal: paddingLarge,
					},
				]}
			>
				{title && (
					<Text large bold>
						{title}
					</Text>
				)}
				{hasCloseButton && !(native && (variant === "Tall" || variant === "Short")) && (
					<CloseModalButton
						containerStyle={{
							position: "absolute",
							right: ignoreHorizontalContainerPadding ? paddingLarge : 0,
							alignSelf: "center",
						}}
					/>
				)}
			</View>
			{children}
		</View>
	);
};

export default ModalWrapperWithHeader;
